import { BsEye, BsEyeSlash, BsCheckCircleFill } from 'react-icons/bs'
import { useTranslation } from 'react-i18next'
import { AppSpan, AppText, Button, FlexColumn, FlexRow, Form } from 'src/style'
import { TextField, Box } from 'src/componentv2'
import { ResetInput } from './types'

interface NewPasswordFormProps {
    resetInput: ResetInput
    setReset: (key: keyof ResetInput) => (value: string) => void
    showNewPassword: boolean
    setShowNewPassword: (value: boolean) => void
    showConfirmPassword: boolean
    setShowConfirmPassword: (value: boolean) => void
    newPasswordFieldError: () => string | undefined
    confirmPasswordFieldError: () => string | undefined
    handleResetPassword: () => void
}

const EYE_ICON_STYLE = {
    position: 'absolute' as const,
    right: '1.6rem',
    top: '3.8rem',
    cursor: 'pointer',
    color: 'rgba(0, 0, 0, 0.5)',
    fontSize: '1.7rem',
}

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
                                                             resetInput, setReset,
                                                             showNewPassword, setShowNewPassword,
                                                             showConfirmPassword, setShowConfirmPassword,
                                                             newPasswordFieldError, confirmPasswordFieldError,
                                                             handleResetPassword
                                                         }) => {
    const { t } = useTranslation()

    const passwordChecks = {
        length: resetInput.newPassword.length >= 8,
        uppercase: /[A-Z]/.test(resetInput.newPassword),
        symbolOrNumber: /[0-9!@#$%^&*(),.?":{}|<>]/.test(resetInput.newPassword),
    }

    const checklistItems = [
        { key: 'length', label: t('login.newPasswordForm.checklist.length'), passed: passwordChecks.length },
        { key: 'uppercase', label: t('login.newPasswordForm.checklist.uppercase'), passed: passwordChecks.uppercase },
        { key: 'symbolOrNumber', label: t('login.newPasswordForm.checklist.symbolOrNumber'), passed: passwordChecks.symbolOrNumber },
    ]

    return (
        <>
            <AppText
                color='#101828' textSize='3' fontWeight='600' bottomPadding='0.7'
                className='login-title'
            >
                {t('login.newPasswordForm.title')}
            </AppText>

            <Form onSubmit={(e: any) => { e.preventDefault(); handleResetPassword() }}>
                <Box topMargin="2.4" style={{ position: 'relative' }}>
                    <TextField
                        label={t('login.newPasswordForm.newPasswordLabel')}
                        placeholder={t('login.newPasswordForm.newPasswordPlaceholder')}
                        type={showNewPassword ? 'text' : 'password'}
                        value={resetInput.newPassword}
                        onChange={setReset('newPassword')}
                        error={newPasswordFieldError()}
                    />
                    <span
                        style={EYE_ICON_STYLE}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                        { showNewPassword ? <BsEye/> : <BsEyeSlash/> }
                    </span>
                </Box>

                <Box topMargin="1.6" style={{ position: 'relative' }}>
                    <TextField
                        label={t('login.newPasswordForm.confirmPasswordLabel')}
                        placeholder={t('login.newPasswordForm.confirmPasswordPlaceholder')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={resetInput.confirmPassword}
                        onChange={setReset('confirmPassword')}
                        error={confirmPasswordFieldError()}
                    />
                    <span
                        style={EYE_ICON_STYLE}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                        { showConfirmPassword ? <BsEye/> : <BsEyeSlash/> }
                    </span>
                </Box>

                <Box
                    topMargin="1.6"
                    style={{
                        backgroundColor: '#E7F6F5',
                        borderRadius: '0.8rem',
                        padding: '1.2rem 1.6rem'
                    }}
                >
                    <FlexColumn alignItems='flex-start'>
                        {checklistItems.map(({ key, label, passed }, i) => (
                            <FlexRow
                                key={key}
                                alignItems='center'
                                justifyContent='flex-start'
                                style={{ gap: '0.6rem', marginTop: i === 0 ? 0 : '0.6rem', width: '100%' }}
                            >
                                <BsCheckCircleFill
                                    style={{
                                        fontSize: '1.4rem',
                                        flexShrink: 0,
                                        color: passed ? '#0D968F' : '#98A2B3'
                                    }}
                                />
                                <AppSpan textSize='1.3' color='#344054' align='left'>
                                    {label}
                                </AppSpan>
                            </FlexRow>
                        ))}
                    </FlexColumn>
                </Box>

                <Button
                    width='100' sizeUnit='%'
                    titleSize='1.6' fontWeight='600'
                    height='4.4' radius='0.8'
                    topMargin='3.5' bottomMargin='3.5' hoverColor='#0D968F'
                    hoverBgColor='#fff' borderColor='#0D968F'
                >
                    {t('login.newPasswordForm.saveButton')}
                </Button>
            </Form>
        </>
    )
}

export default NewPasswordForm